import {
    useCallback,
    useMemo,
} from 'react'
import noop from 'lodash/noop'

import Radio from 'app/shared-components/Radio'
import Checkbox from 'app/shared-components/Checkbox'

import formatSensorData from 'app/utils/formatSensorData'
import DownloadOptions from 'app/shared-components/DownloadOptions'
import useLabel from 'app/hooks/useLabel'

import useStyles from './TemperatureChartOptions.style'

const EMPTY_ARRAY = []

type Props = {
    showTemperatureRangeOption?: boolean,
    showPdfButton?: boolean,
    showCsvButton?: boolean,
    showTemperatureRangeAllOptions?: boolean,
    sensorData?: any[],
    sensorCodes?: any[],
    showTempRange: boolean,
    onCheckShowTempRange: (value: boolean) => void,
    serialNumber?: string,
    printChart?: () => void,
    selectedTemperatureRange?: string,
    radioOptions?: {
        label: string,
        value: string,
    }[],
    onChangeSelectedTemperatureRange?: (value: string) => void,
    isContainer?: boolean,
    showDoorEventOption?: boolean,
    showDoorEvent?: boolean,
    onCheckShowDoorEvent?: (value: boolean) => void,
    showVirtualLogger?: boolean,
    setShowVirtualLogger?: (value: boolean) => void,
    showIncludeInvalidOption?: boolean,
    showIncludeInvalid?: boolean,
    setShowIncludeInvalid?: (value: boolean) => void,
}

const TemperatureChartOptions = ({
    showTemperatureRangeOption = false,
    showPdfButton = false,
    showCsvButton = false,
    showTemperatureRangeAllOptions = false,
    serialNumber = '',
    showTempRange,
    onCheckShowTempRange,
    sensorData,
    sensorCodes,
    printChart = noop,
    selectedTemperatureRange = '',
    radioOptions = EMPTY_ARRAY,
    onChangeSelectedTemperatureRange = noop,
    isContainer = false,
    showDoorEventOption = false,
    showDoorEvent = false,
    onCheckShowDoorEvent = noop,
    showVirtualLogger = false,
    setShowVirtualLogger,
    showIncludeInvalidOption = false,
    showIncludeInvalid = false,
    setShowIncludeInvalid,
}: Readonly<Props>) => {
    const l = useLabel()
    const {
        classes,
    } = useStyles()

    const sensorDataUpdated = useMemo(() => {
        if (!sensorData) {
            return undefined
        }

        return formatSensorData(sensorData)
    }, [sensorData])

    const virtualLoggerOnChange = useCallback((value: boolean) => {
        if (value) {
            onCheckShowDoorEvent(false)
        }

        setShowVirtualLogger(value)
    }, [
        setShowVirtualLogger,
        onCheckShowDoorEvent,
    ])

    return (
        <DownloadOptions
            sensorData={sensorDataUpdated}
            sensorCodes={sensorCodes}
            serialNumber={serialNumber}
            printChart={printChart}
            isContainer={isContainer}
            showPdfButton={showPdfButton}
            showCsvButton={showCsvButton}
        >
            {showDoorEventOption && (
                <Checkbox
                    data-testid="door-event"
                    value={showDoorEvent}
                    onChange={onCheckShowDoorEvent}
                    title={l('DOOR_EVENTS')}
                    name="DoorEvents"
                    disabled={showVirtualLogger}
                />
            )}
            {showTemperatureRangeOption && (
                <Checkbox
                    data-testid="temp-range"
                    value={showTempRange}
                    onChange={onCheckShowTempRange}
                    title={l('TEMPERATURE_RANGE')}
                    name="temperatureRange"
                />
            )}
            {showTemperatureRangeAllOptions && (
                <Radio
                    className={classes.selectedTemperatureRange}
                    name="selectedTemperatureRange"
                    compact
                    value={selectedTemperatureRange}
                    options={radioOptions}
                    onChange={onChangeSelectedTemperatureRange}
                />
            )}
            {setShowVirtualLogger && (
                <Checkbox
                    name="virtualLogger"
                    title={l('VIRTUAL_LOGGER')}
                    value={showVirtualLogger}
                    onChange={virtualLoggerOnChange}
                />
            )}
            {showIncludeInvalidOption && (
                <Checkbox
                    name="includeInvalid"
                    title={l('INCLUDE_INVALID')}
                    value={showIncludeInvalid}
                    onChange={setShowIncludeInvalid}
                />
            )}
        </DownloadOptions>
    )
}

export default TemperatureChartOptions
